import React, { cloneElement, Children, isValidElement } from 'react';
import type TTabs from './types/TTabs';
import Box from '../Box/Box';
import keyboardKeys from '../../constants/keyboardKeys';
import { isRightOrLeftArrow } from '../../utils/keyboard';

/**
 * TODO: We should change the implementation of this component and remove Children.map
 * https://react.dev/reference/react/Children#accepting-an-array-of-objects-as-a-prop
 */

export function getSiblingAccessor(key: string): string {
  const sibling = {
    [keyboardKeys.ARROW_RIGHT]: 'nextElementSibling',
    [keyboardKeys.ARROW_LEFT]: 'previousElementSibling',
  };
  return sibling[key as keyof typeof sibling];
}

function isValidHTMLNextTab(nextTab: HTMLElement | null): boolean {
  return nextTab instanceof HTMLElement;
}

function getNextTab(target: HTMLElement, siblingAccessor: string): HTMLElement | null {
  if (!isValidHTMLNextTab(target)) return null;
  const nextTab = target?.[siblingAccessor] as HTMLElement;
  if (!isValidHTMLNextTab(nextTab)) return null;
  return nextTab;
}

export function focusPrevNextTab(event: React.KeyboardEvent<Element>) {
  if (!(event?.target instanceof HTMLElement)) return;
  getNextTab(event.target, getSiblingAccessor(event.key))?.focus();
}

export function isActiveTab(activeTab: number) {
  return (tabIdx: number) => tabIdx === activeTab;
}

type TValidateTabCount =
  | string
  | number
  | boolean
  | React.ReactElement<any, string | React.JSXElementConstructor<any>>
  | Iterable<React.ReactNode>
  | React.ReactPortal
  | null
  | undefined;

function validateTabCount(children: TValidateTabCount) {
  const maxTabs = 7;
  if (React.Children.toArray(children)?.length > maxTabs) {
    throw new Error('Tabs component should not have more than 7 tabs');
  }
}

const Tabs = ({ children, setActiveTab, activeTab = 0, isFluid, ...otherProps }: TTabs) => {
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!isRightOrLeftArrow(event.key)) return;

    event.preventDefault();
    focusPrevNextTab(event);
  };

  validateTabCount(children);

  const makeOnClickHandler = (tabIdx: number) => () => setActiveTab?.(tabIdx);

  return (
    <Box
      as="div"
      name="TabsNext"
      modifiers={isFluid ? ['fluid'] : []}
      role="tablist"
      onKeyDown={handleKeyDown}
      aria-label="Tabs bar"
      {...otherProps}
    >
      {Children.map(children, (tab, tabIdx) => {
        return (
          isValidElement(tab) &&
          cloneElement(tab, {
            isActive: isActiveTab(activeTab)(tabIdx),
            onClick: makeOnClickHandler(tabIdx),
            ...tab.props,
            id: tabIdx,
          })
        );
      })}
    </Box>
  );
};

export default Tabs;
